import { ReactNode, HTMLAttributes } from 'react';
import { Controller, Control, RegisterOptions } from 'react-hook-form';
import { MarketField, MarketInputText } from '@market/react';
import { useTranslation } from 'react-i18next';

export const INPUT_TEXT_TEST_ID = 'input-text-test-id';

type ControlledInputTextProps = HTMLAttributes<HTMLElement> & {
  // fields used by the controller
  control: Control;
  defaultValue?: string;
  name: string;
  rules?: RegisterOptions;
  shouldUnregister?: boolean;

  // fields used by Market components, there are more that are not listed here
  // visit go/marketstorybook to see all available components and their props
  bottomAccessory?: string;
  cta?: string;
  disabled?: boolean;
  children?: ReactNode;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  onClick?: () => void;

  // custom prop used to transform input value
  // see: https://github.com/squareup/solidshop/pull/6913
  transform?: (_value: string) => string;

  // the rules prop is not used if a custom resolver is provided
  // this will be set as false by default as most of our controlled inputs will be used in forms
  // if you are using this component outside of a form, set this to true to use the rules prop
  useRules?: boolean;
};

export const ControlledInputText = ({
  className,
  control,
  defaultValue = '',
  name,
  rules = {},
  shouldUnregister = false,
  bottomAccessory,
  children,
  cta,
  disabled = false,
  label,
  placeholder,
  maxLength,
  minLength,
  onClick = undefined,
  transform = (value) => value,
  useRules = false,
}: ControlledInputTextProps) => {
  const { i18n, t } = useTranslation();

  if (useRules) {
    // small hack because `required` doesn't doesn't do a whitespace check
    if (rules?.required) {
      // eslint-disable-next-line no-param-reassign
      rules.validate = (value) => Boolean(value.trim());
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      {...(useRules && { rules })}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
        formState: { isSubmitting },
      }) => {
        return (
          <>
            <MarketField
              className={className}
              disabled={disabled || isSubmitting}
              name={name}
              invalid={Boolean(error)}
            >
              <MarketInputText
                onBlur={onBlur}
                onClick={onClick}
                data-testid={INPUT_TEXT_TEST_ID}
                name={name}
                value={value}
                placeholder={placeholder}
                maxlength={maxLength}
                minlength={minLength}
                onMarketInputValueChange={(e) => {
                  const userInput = e.detail.value;
                  const transformedInput = transform(userInput);
                  // eslint-disable-next-line no-param-reassign
                  e.target.shadowRoot.querySelector('input').value =
                    transformedInput;
                  onChange(transformedInput);
                }}
              >
                <label>{label}</label>
                {children}
              </MarketInputText>
              {bottomAccessory && (
                <small slot="bottom-accessory">{bottomAccessory}</small>
              )}
              {error && (
                <small slot="error">
                  {error?.message && i18n.exists(error.message)
                    ? t(error.message, { label: label.toLowerCase() })
                    : t('error.validation.invalidValue')}
                </small>
              )}
            </MarketField>
            {cta && <span>{cta}</span>}
          </>
        );
      }}
    />
  );
};

export default ControlledInputText;
